import { create } from 'zustand';
import { Event } from '../types/event.types';

/**
 * 🔄 Modal Stack Store - Phase 4 PRD Navigation Back Intelligente
 *
 * Source de vérité unique pour la gestion des modales.
 * Synchronisé avec history.pushState / popstate via useModalStack hook.
 *
 * Pattern inspiré de Instagram/Airbnb : stack centralisée de modales.
 */

// ============================================================================
// TYPES
// ============================================================================

// 🔄 PHASE 4 : Type flexible pour les événements enrichis
// Permet de passer soit un Event complet soit un objet partiel avec les champs essentiels
export interface EnrichedEvent {
  main: Event | {
    id: string;
    seriesId?: string;
    title: string;
    datetime?: Date;
    location?: string;
    latitude?: number;
    longitude?: number;
    updatedAt?: Date; // Pour la comparaison dans React.memo
  };
  series: Array<Event | { id: string; datetime?: Date; title?: string }>;
  _signature?: string;
}

export interface PreviewCardMapData {
  events: Event[];
  enrichedEvents?: EnrichedEvent[];
  location: string;
  userLocation?: { lat: number; lng: number } | null;
  isMapView: boolean;
}

export interface EventModalData {
  event: Event;
  seriesEvents?: Event[];
  fromPreviewCardMap: boolean;
  isMapView?: boolean;
  userLocation?: { lat: number; lng: number } | null;
}

export type ModalType = 'previewCardMap' | 'eventModal';

export interface ModalEntry {
  id: string; // Unique ID pour tracking
  type: ModalType;
  data: PreviewCardMapData | EventModalData;
  url: string;
  timestamp: number;
}

// ============================================================================
// STORE STATE
// ============================================================================

interface ModalStackState {
  // La stack de modales (dernier = top)
  stack: ModalEntry[];

  // Compteur pour générer des IDs uniques
  _idCounter: number;

  // Actions internes (utilisées par le hook useModalStack)
  _push: (entry: Omit<ModalEntry, 'id' | 'timestamp'>) => ModalEntry;
  _pop: () => ModalEntry | undefined;
  _clear: () => void;
  _replaceTop: (entry: Omit<ModalEntry, 'id' | 'timestamp'>) => ModalEntry | undefined;

  // Action pour synchronisation popstate (quand l'URL change sans notre contrôle)
  _syncWithUrl: (currentUrl: string) => void;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useModalStackStore = create<ModalStackState>((set, get) => ({
  stack: [],
  _idCounter: 0,

  _push: (entry) => {
    const id = `modal-${get()._idCounter}`;
    const newEntry: ModalEntry = {
      ...entry,
      id,
      timestamp: Date.now()
    };

    set((state) => ({
      stack: [...state.stack, newEntry],
      _idCounter: state._idCounter + 1
    }));

    console.log('📥 [ModalStack] PUSH:', {
      type: newEntry.type,
      url: newEntry.url,
      stackSize: get().stack.length
    });

    return newEntry;
  },

  _pop: () => {
    const stack = get().stack;
    if (stack.length === 0) {
      console.log('📤 [ModalStack] POP: Stack vide, rien à pop');
      return undefined;
    }

    const popped = stack[stack.length - 1];
    set({ stack: stack.slice(0, -1) });

    console.log('📤 [ModalStack] POP:', {
      type: popped.type,
      url: popped.url,
      stackSize: get().stack.length
    });

    return popped;
  },

  _clear: () => {
    const previousSize = get().stack.length;
    set({ stack: [] });
    console.log('🧹 [ModalStack] CLEAR:', { previousSize });
  },

  _replaceTop: (entry) => {
    const stack = get().stack;
    if (stack.length === 0) {
      console.log('🔄 [ModalStack] REPLACE_TOP: Stack vide, conversion en PUSH');
      return get()._push(entry);
    }

    const id = `modal-${get()._idCounter}`;
    const newEntry: ModalEntry = {
      ...entry,
      id,
      timestamp: Date.now()
    };

    set((state) => ({
      stack: [...state.stack.slice(0, -1), newEntry],
      _idCounter: state._idCounter + 1
    }));

    console.log('🔄 [ModalStack] REPLACE_TOP:', {
      type: newEntry.type,
      url: newEntry.url,
      stackSize: get().stack.length
    });

    return newEntry;
  },

  _syncWithUrl: (currentUrl: string) => {
    const stack = get().stack;
    const topModal = stack[stack.length - 1];

    // Si l'URL ne correspond plus au top de la stack, on doit synchroniser
    if (topModal && topModal.url !== currentUrl) {
      // Chercher si l'URL correspond à un élément plus bas dans la stack
      const matchIndex = stack.findIndex(m => m.url === currentUrl);

      if (matchIndex >= 0) {
        // L'URL correspond à un élément de la stack, on tronque jusqu'à lui
        const newStack = stack.slice(0, matchIndex + 1);
        set({ stack: newStack });
        console.log('🔗 [ModalStack] SYNC: Tronqué jusqu\'à index', matchIndex);
      } else if (currentUrl === '/' || currentUrl === '') {
        // Retour à la home, on vide tout
        set({ stack: [] });
        console.log('🔗 [ModalStack] SYNC: Retour home, stack vidée');
      } else {
        // URL inconnue, on pop le top
        get()._pop();
        console.log('🔗 [ModalStack] SYNC: URL inconnue, pop top');
      }
    }
  }
}));

// ============================================================================
// SELECTORS (pour éviter les re-renders inutiles)
// ============================================================================

export const useModalStack = () => useModalStackStore((s) => s.stack);
export const useModalStackSize = () => useModalStackStore((s) => s.stack.length);

export const useTopModal = () => useModalStackStore((s) => {
  const stack = s.stack;
  return stack.length > 0 ? stack[stack.length - 1] : null;
});

export const useIsPreviewCardMapOpen = () => useModalStackStore((s) =>
  s.stack.some(m => m.type === 'previewCardMap')
);

export const useIsEventModalOpen = () => useModalStackStore((s) => {
  const stack = s.stack;
  const top = stack.length > 0 ? stack[stack.length - 1] : null;
  return top?.type === 'eventModal';
});

export const usePreviewCardMapData = () => useModalStackStore((s) => {
  const entry = s.stack.find(m => m.type === 'previewCardMap');
  return entry ? entry.data as PreviewCardMapData : null;
});

export const useEventModalData = () => useModalStackStore((s) => {
  const stack = s.stack;
  const top = stack.length > 0 ? stack[stack.length - 1] : null;
  return top?.type === 'eventModal' ? top.data as EventModalData : null;
});
